import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { STAFF_ROLES } from '@bulk/types';
import { useAuth } from '@/lib/auth';
import { useAddress, useHostContext, type HostGym } from '@/lib/host';
import { PageLoader } from '@/components/ui';
import { safeNext } from '@/surfaces/auth/next';
import { PortalLookProvider } from '@/surfaces/shared/PortalLook';

const PublicSite = lazy(() => import('@/surfaces/public/PublicSite'));
const AuthPages = lazy(() => import('@/surfaces/auth/AuthPages'));
const MemberArea = lazy(() => import('@/surfaces/member/MemberArea'));
const AdminArea = lazy(() => import('@/surfaces/admin/AdminArea'));
const PlatformArea = lazy(() => import('@/surfaces/platform/PlatformArea'));
const MockPayPage = lazy(() => import('@/surfaces/member/MockPayPage'));
const InvitePage = lazy(() => import('@/surfaces/auth/InvitePage'));
const MemberLinkPage = lazy(() => import('@/surfaces/auth/MemberLinkPage'));
const OffersStopPage = lazy(() => import('@/surfaces/auth/OffersStopPage'));
const ReceiptPage = lazy(() => import('@/surfaces/shared/ReceiptPage'));
const WebsiteCanvas = lazy(() => import('@/surfaces/admin/pages/website/canvas/CanvasFrame').then((m) => ({ default: m.WebsiteCanvas })));
const StudioCanvas = lazy(() => import('@/surfaces/admin/pages/website/canvas/CanvasFrame').then((m) => ({ default: m.StudioCanvas })));
const SignInElsewhere = lazy(() => import('@/surfaces/auth/SignInElsewhere').then((m) => ({ default: m.SignInElsewhere })));

export function RequireAuth({ children, roles }: { children: React.ReactNode; roles?: Array<'staff' | 'member' | 'platform'> }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const host = useHostContext().data;
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (user.signInElsewhere) return <SignInElsewhere addresses={user.signInAddresses} brand={host?.hostGym?.name ?? null} next={safeNext(location.pathname + location.search)} />;
  if (roles?.includes('platform') && !user.isPlatformAdmin) return <Navigate to="/app" replace />;
  if (roles?.includes('staff') && user.viewAs === 'MEMBER') return <Navigate to="/app" replace />;
  const staffHere = STAFF_ROLES.includes(user.currentGym?.role as (typeof STAFF_ROLES)[number]);
  if (roles?.includes('staff') && !staffHere && !user.isPlatformAdmin && !user.suspendedGym) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/** At a staff address the member area opens only while a practice gym's member area is viewed as one of its made-up members. */
function MemberAreaRoute({ hostGym, staffAddress }: { hostGym: HostGym | null; staffAddress: boolean }) {
  const { user, viewAs } = useAuth();
  if (staffAddress && (viewAs !== 'MEMBER' || (user && user.viewAs !== 'MEMBER'))) return <Navigate to="/admin" replace />;
  return (
    <RequireAuth>
      <MemberArea hostGym={hostGym} />
    </RequireAuth>
  );
}

const PORTAL_LOOK_PATHS = /^\/(login|register|invite|member-link|offers|app)(\/|$)/;

export function App() {
  const host = useHostContext();
  const { portal, strict } = useAddress();
  const { pathname } = useLocation();
  if (host.isLoading) return <PageLoader />;
  const hostGym = host.data?.hostGym ?? null;
  const staffAddress = strict && (portal === 'SERVICE' || portal === 'ADMIN');

  // A route a visitor can open on a gym's site address goes into `APP_PATH` in apps/api/src/routes/web-app.ts too, or the server answers it with 404.
  return (
    <PortalLookProvider look={host.data?.look ?? null} active={PORTAL_LOOK_PATHS.test(pathname)}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<AuthPages mode="login" hostGym={hostGym} portal={portal} strict={strict} />} />
          <Route path="/register" element={<AuthPages mode="register" hostGym={hostGym} portal={portal} strict={strict} />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/member-link/:token" element={<MemberLinkPage hostGym={hostGym} />} />
          <Route path="/offers/stop/:token" element={<OffersStopPage hostGym={hostGym} />} />
          <Route path="/pay/mock/:paymentId" element={<RequireAuth><MockPayPage /></RequireAuth>} />
          <Route path="/receipts/:paymentId" element={<RequireAuth><ReceiptPage /></RequireAuth>} />
          <Route path="/app/*" element={<MemberAreaRoute hostGym={hostGym} staffAddress={staffAddress} />} />
          <Route path="/admin/website/canvas" element={<RequireAuth roles={['staff']}><WebsiteCanvas /></RequireAuth>} />
          <Route path="/admin/*" element={<RequireAuth roles={['staff']}><AdminArea hostGym={hostGym} /></RequireAuth>} />
          <Route path="/platform/gyms/:gymId/design/canvas" element={<RequireAuth roles={['platform']}><StudioCanvas /></RequireAuth>} />
          <Route path="/platform/*" element={<RequireAuth roles={['platform']}><PlatformArea /></RequireAuth>} />
          <Route path="/*" element={staffAddress ? <Navigate to="/admin" replace /> : <PublicSite hostGym={hostGym} />} />
        </Routes>
      </Suspense>
    </PortalLookProvider>
  );
}
